import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Plus, PiggyBank, Target, Trophy, Hand, Trash2 } from 'lucide-react'
import { accountService, goalService } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { formatRupees } from '../utils/currency'

const GoalsPage = () => {
  const [loading, setLoading] = useState(true)
  const [goals, setGoals] = useState([])
  const [goalStats, setGoalStats] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    target_amount: '',
    current_amount: '',
    target_date: '',
    priority: 'medium',
    category: 'Savings',
    notes: '',
  })

  const accountMap = useMemo(() => {
    return accounts.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
  }, [accounts])

  const loadData = async () => {
    try {
      setLoading(true)
      const [goalData, statsData, accountData] = await Promise.all([
        goalService.getGoals({ include_contributions: false }),
        goalService.getStats(),
        accountService.getAccounts(),
      ])

      setGoals(goalData.goals || [])
      setGoalStats(statsData || null)
      setAccounts(accountData.accounts || [])
    } catch (error) {
      toast.error(error.message || 'Failed to load goals')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleCreateGoal = async (e) => {
    e.preventDefault()

    try {
      await goalService.createGoal({
        ...formData,
        target_amount: Number(formData.target_amount),
        current_amount: Number(formData.current_amount || 0),
      })
      toast.success('Goal created successfully')
      setShowCreateModal(false)
      setFormData({
        name: '',
        target_amount: '',
        current_amount: '',
        target_date: '',
        priority: 'medium',
        category: 'Savings',
        notes: '',
      })
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to create goal')
    }
  }

  const handleContribute = async (goal) => {
    const amountInput = window.prompt(`Contribution amount for ${goal.name}`, '500')
    if (!amountInput) return

    const accountOptions = accounts.map((acc) => `${acc.id}: ${acc.name}`).join('\n')
    const accountInput = window.prompt(
      `Optional account ID to deduct from (leave blank for none):\n${accountOptions}`,
      ''
    )

    try {
      await goalService.contributeGoal(goal.id, {
        amount: Number(amountInput),
        account_id: accountInput || null,
      })
      toast.success('Contribution added')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to contribute')
    }
  }

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return

    try {
      await goalService.deleteGoal(goalId)
      toast.success('Goal deleted')
      await loadData()
    } catch (error) {
      toast.error(error.message || 'Failed to delete goal')
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading goals..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Goals & Sinking Funds</h1>
          <p className="text-gray-600 dark:text-gray-400">Build disciplined savings with milestone tracking.</p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Goal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Goals</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{goalStats?.total_goals || goals.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Active Goals</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{goalStats?.active_goals || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{goalStats?.completed_goals || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Active Progress</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{(goalStats?.active_progress_percent || 0).toFixed(1)}%</p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Goals</h2>
        {goals.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No goals found. Create one to start planning.</p>
        ) : (
          <div className="space-y-4">
            {goals.map((goal) => {
              const progress = Number(goal.progress_percent || 0)
              const isCompleted = goal.status === 'completed'

              return (
                <div key={goal.id} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 dark:text-white">{goal.name}</p>
                        {isCompleted ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 inline-flex items-center gap-1">
                            <Trophy className="h-3 w-3" /> Completed
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 inline-flex items-center gap-1">
                            <Target className="h-3 w-3" /> {goal.priority || 'medium'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {goal.category || 'Savings'} • Target by {goal.target_date ? new Date(goal.target_date).toLocaleDateString() : 'no deadline'}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{formatRupees(goal.current_amount)} / {formatRupees(goal.target_amount)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{progress.toFixed(1)}%</p>
                    </div>
                  </div>

                  <div className="mt-3 h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                    <div
                      className={`h-full ${isCompleted ? 'bg-emerald-500' : 'bg-primary-500'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-end gap-2">
                    {!isCompleted && (
                      <button
                        onClick={() => handleContribute(goal)}
                        className="text-sm px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                      >
                        <Hand className="h-3.5 w-3.5" />
                        Contribute
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteGoal(goal.id)}
                      className="text-sm px-3 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200"
                    >
                      <Trash2 className="h-3.5 w-3.5 inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Contribution Accounts</h2>
        {accounts.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No accounts available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {accounts.map((account) => (
              <div key={account.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                <p className="font-medium text-gray-900 dark:text-white">{account.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{account.id}</p>
                <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{formatRupees(account.balance)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Create Goal</h3>
            <form onSubmit={handleCreateGoal} className="space-y-4">
              <input
                required
                className="input-field"
                placeholder="Goal name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />

              <input
                required
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                placeholder="Target amount"
                value={formData.target_amount}
                onChange={(e) => setFormData((prev) => ({ ...prev, target_amount: e.target.value }))}
              />

              <input
                type="number"
                min="0"
                step="0.01"
                className="input-field"
                placeholder="Current amount (optional)"
                value={formData.current_amount}
                onChange={(e) => setFormData((prev) => ({ ...prev, current_amount: e.target.value }))}
              />

              <input
                type="date"
                className="input-field"
                value={formData.target_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, target_date: e.target.value }))}
              />

              <select
                className="input-field"
                value={formData.priority}
                onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
              >
                <option value="low">Low priority</option>
                <option value="medium">Medium priority</option>
                <option value="high">High priority</option>
                <option value="critical">Critical priority</option>
              </select>

              <input
                className="input-field"
                placeholder="Category"
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
              />

              <textarea
                className="input-field"
                rows={2}
                placeholder="Notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              />

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1 inline-flex items-center justify-center gap-2">
                  <PiggyBank className="h-4 w-4" />
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default GoalsPage
